import React, { Fragment, useState, useEffect } from 'react';
import { Input, Button } from "@material-tailwind/react";
import { Dialog, Transition } from '@headlessui/react';
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/24/solid";
import { useTranslation } from 'react-i18next';
import axiosInstance from "../axios/axiosInstance";
import {
  ActionButton,
  DataTable,
  PageCard,
  PageShell,
  PageToolbar,
  RowIconButton,
} from "../components/ui";

export default function Roles() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    fetchRoles();
  }, []); 

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get("/admin/roles");
      setRoles(response.data.data);
    } catch (error) {
      console.error("Ошибка при получении списка ролей:", error);
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    setNewRole('');
  };

  const openModal = () => {
    setIsOpen(true);
  };

  const handleSave = async () => {
    try {
      await axiosInstance.post("/admin/roles", { name: newRole });
      fetchRoles(); 
      closeModal();
    } catch (error) {
      console.error("Ошибка при сохранении роли:", error);
    }
  };

  const handleDelete = async (roleId) => {
    try {
      await axiosInstance.delete(`/admin/roles/${roleId}`);
      fetchRoles(); // После удаления роли обновляем список ролей
    } catch (error) {
      console.error("Ошибка при удалении роли:", error);
    }
  };

  const columns = [
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name) => <span className="text-sm font-medium text-slate-800">{name}</span>,
    },
    {
      title: t('common.actions'),
      key: 'action',
      width: 88,
      fixed: 'right',
      render: (_, role) => (
          <RowIconButton onClick={() => handleDelete(role.id)}>
            <TrashIcon className="h-[18px] w-[18px]" />
          </RowIconButton>
      ),
    },
  ];

  const renderMobileCard = (role) => (
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 text-sm font-semibold text-[#1D2C7B] truncate">{role.name}</span>
        <RowIconButton onClick={() => handleDelete(role.id)}>
          <TrashIcon className="h-[18px] w-[18px]" />
        </RowIconButton>
      </div>
  );

  return (
    <PageShell>
      <PageCard>
        <PageToolbar
          title={t('roles.title')}
          count={roles.length}
          actions={
            <ActionButton icon={<PlusCircleIcon className="h-4 w-4" />} onClick={openModal}>
              {t('common.add')}
            </ActionButton>
          }
        />

        <DataTable
          columns={columns}
          dataSource={roles}
          mobileCard={renderMobileCard}
          scrollX={420}
          pagination={{
            showSizeChanger: false,
            position: ['bottomCenter'],
            className: 'px-4 pb-3',
          }}
        />
      </PageCard>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-2.5 sm:p-4 text-center">

              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl border border-slate-200/90 bg-white p-4 sm:p-6 text-left align-middle transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-base font-semibold leading-6 text-slate-800"
                  >
                    {t('roles.create')}
                  </Dialog.Title>
                  <div className="mt-4">
                    <Input label={t('common.name')} size="lg" value={newRole} onChange={(e) => setNewRole(e.target.value)} />
                  </div>

                  <div className="mt-5">
                    <Button
                      onClick={handleSave}
                      fullWidth
                      className="normal-case font-normal"
                      style={{ backgroundColor: '#1D2C7B' }}
                    >
                      {t('common.save')}
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </PageShell>
  );
}
